/**
 * The plan, ranked by colour so the valuable accounts stand out: gold for
 * Ultimate, silver for Premium, grey for the free tiers.
 *
 * The tier colour is carried by the dot, the tint and the border — never by the
 * text, which stays `text-foreground`. A coloured label has to be re-checked
 * for contrast against every background and in both themes, and the first
 * version of this badge got that wrong: the text came out the same value as its
 * own tint and the plan was unreadable.
 *
 * Matched on a substring because upstream names vary — "Ultimate",
 * "Dashboard Ultimate" and "UltimateSmartInvestorBundle" are all the top tier.
 */
import { cn } from "@/lib/utils";

type Tier = { match: RegExp; dot: string; tint: string; border: string };

const TIERS: Tier[] = [
  // Gold.
  { match: /ultimate/i, dot: "#d4a017", tint: "#d4a01722", border: "#d4a01780" },
  // Silver.
  { match: /premium/i, dot: "#8c98a8", tint: "#8c98a822", border: "#8c98a880" },
  // Bronze — TR+ sits between Premium and the free tiers.
  { match: /tr\s*\+|plus/i, dot: "#b06c34", tint: "#b06c3422", border: "#b06c3480" },
];

export function PlanBadge({
  plan,
  className,
}: {
  plan: string | null;
  className?: string;
}) {
  const tier = plan ? TIERS.find((t) => t.match.test(plan)) : undefined;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold text-foreground",
        // Free/Basic and anything unrecognised: plain, using theme tokens.
        !tier && "border-border bg-muted",
        className
      )}
      style={
        tier
          ? { backgroundColor: tier.tint, borderColor: tier.border }
          : undefined
      }
    >
      {tier && (
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: tier.dot }}
        />
      )}
      {plan ?? "No plan"}
    </span>
  );
}
